/**
 * Revise-and-resubmit (SCH.5, PM side).
 *
 * When a supervisor sends a request back, the PM sees their note + proposed date
 * and resubmits with a revised desired window via this calendar dialog. Clears
 * the bounce and re-enters the supervisor's review queue.
 */
import { useState } from 'react';
import { Loader2, SendHorizonal } from 'lucide-react';
import { toast } from 'sonner';
import type { ScheduleDay } from '@vari-crew/schemas';
import { useResubmitProject } from '@/api/projects';
import { Button } from '@/components/ui/Button';
import { CalendarDatePicker } from '@/components/ui/CalendarDatePicker';
import { DailyHoursPicker, isWeekendDay } from '@/components/ui/DailyHoursPicker';
import { Dialog } from '@/components/ui/Dialog';
import { dayToIso, isoToDay, parseDay } from '@/lib/calendarGrid';
import { formatMoneyCents } from '@/lib/format';

export interface ResubmitButtonProps {
  project: {
    id: string;
    title: string;
    requested_start_date: string | null;
    requested_end_date?: string | null;
    requested_schedule?: ScheduleDay[] | null;
    revision_note: string | null;
    revision_proposed_start: string | null;
    /** Budget the supervisor chose to share on the bounce (§A6c). */
    revision_show_budget?: boolean;
    bid_total_cents?: number | null;
  };
}

function fmt(day: string | null): string {
  return day
    ? parseDay(day).toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      })
    : '';
}

export function ResubmitButton({ project }: ResubmitButtonProps) {
  const resubmit = useResubmitProject(project.id);
  // Default to the supervisor's proposed day if they offered one, else the
  // PM's original requested start.
  const initStart = isoToDay(project.revision_proposed_start ?? project.requested_start_date);
  const initEnd = isoToDay(project.requested_end_date ?? null);
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState<string | null>(initStart);
  const [end, setEnd] = useState<string | null>(initEnd);
  // Revised dates need revised hours: the picker reconciles the original
  // per-day hours onto the new days, so the request's "Hours" list can't end
  // up contradicting its dates (VCS-202). The API derives the start/end
  // bounds (incl. time-of-day) from this schedule when present.
  const [schedule, setSchedule] = useState<ScheduleDay[] | null>(
    project.requested_schedule ?? null,
  );
  const [includeWeekends, setIncludeWeekends] = useState(() =>
    (project.requested_schedule ?? []).some((s) => isWeekendDay(s.date)),
  );

  function save() {
    resubmit.mutate(
      {
        requested_start_date: start ? dayToIso(start) : null,
        requested_end_date: end ? dayToIso(end) : null,
        requested_schedule: schedule && schedule.length > 0 ? schedule : null,
      },
      {
        onSuccess: () => {
          toast.success(`"${project.title}" resubmitted`);
          setOpen(false);
        },
        onError: (err) => toast.error(`Resubmit failed: ${err.message}`),
      },
    );
  }

  return (
    <>
      <Button
        size="sm"
        onClick={() => {
          setStart(initStart);
          setEnd(initEnd);
          setSchedule(project.requested_schedule ?? null);
          setIncludeWeekends((project.requested_schedule ?? []).some((s) => isWeekendDay(s.date)));
          setOpen(true);
        }}
      >
        <SendHorizonal className="w-3.5 h-3.5" />
        Revise &amp; resubmit
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        busy={resubmit.isPending}
        title={'Revise & resubmit'}
      >
        <div className="p-4 space-y-3">
                <div className="font-medium text-sm truncate">{project.title}</div>
                {project.revision_note && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900">
                    <span className="font-medium">Supervisor: </span>
                    {project.revision_note}
                    {project.revision_proposed_start && (
                      <div className="mt-1">
                        Proposed: {fmt(isoToDay(project.revision_proposed_start))}
                      </div>
                    )}
                    {project.revision_show_budget && project.bid_total_cents != null && (
                      <div className="mt-1">
                        Budget: <span className="font-medium">{formatMoneyCents(project.bid_total_cents)}</span>
                      </div>
                    )}
                  </div>
                )}
                <CalendarDatePicker
                  start={start}
                  end={end}
                  onChange={(r) => {
                    setStart(r.start);
                    setEnd(r.end);
                  }}
                  includeWeekends={includeWeekends}
                  onIncludeWeekendsChange={setIncludeWeekends}
                />
                <DailyHoursPicker
                  start={start}
                  end={end}
                  value={schedule}
                  onChange={setSchedule}
                  includeWeekends={includeWeekends}
                />
                <div className="flex justify-end gap-2 pt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setOpen(false)}
                    disabled={resubmit.isPending}
                  >
                    Cancel
                  </Button>
                  <Button size="sm" onClick={save} disabled={resubmit.isPending || !start}>
                    {resubmit.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    Resubmit
                  </Button>
                </div>
        </div>
      </Dialog>
    </>
  );
}
